import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, RotateCcw, Sparkles } from 'lucide-react';

import { Modal } from '@/components/modal';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useNotification } from '@/components/notifications';
import {
  CollectionChatPromptResponse,
  CollectionChatPromptSource,
  getCollectionChatSystemPrompt,
} from '@/services/data-sources/get-collection-chat-system-prompt';
import { updateCollectionChatSystemPrompt } from '@/services/data-sources/update-collection-chat-system-prompt';

interface CollectionChatPromptModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId?: string | null;
  collectionId?: string | null;
  collectionName?: string | null;
  onPromptUpdated?: (
    source: CollectionChatPromptSource,
    promptId: string | null,
  ) => void;
}

const EMPTY_Prompt: CollectionChatPromptResponse = {
  prompt: '',
  source: 'default',
  prompt_id: null,
};

export const CollectionChatPromptModal = ({
  open,
  onOpenChange,
  orgId,
  collectionId,
  collectionName,
  onPromptUpdated,
}: CollectionChatPromptModalProps) => {
  const notification = useNotification();
  const [promptData, setPromptData] =
    useState<CollectionChatPromptResponse>(EMPTY_Prompt);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmingClear, setConfirmingClear] = useState(false);

  const targetLabel = collectionName || 'this collection';

  const loadPrompt = useCallback(async () => {
    if (!open || !orgId || !collectionId) {
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const result = await getCollectionChatSystemPrompt(orgId, collectionId);
      if ('detail' in result) {
        setError(result.detail);
        return;
      }
      setPromptData(result);
      setInputValue(result.prompt);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Unable to load chat system prompt.',
      );
    } finally {
      setIsLoading(false);
    }
  }, [collectionId, open, orgId]);

  useEffect(() => {
    if (open) {
      void loadPrompt();
    } else {
      setInputValue('');
      setError(null);
      setPromptData(EMPTY_Prompt);
    }
  }, [loadPrompt, open]);

  const hasCustomPrompt = promptData.source === 'custom';

  const helperText = useMemo(() => {
    if (hasCustomPrompt) {
      return 'A custom prompt is currently active. Update the text below to change it or clear the field to restore the default.';
    }
    return 'You are viewing the default system prompt. Customize it below to tailor the assistant for this collection.';
  }, [hasCustomPrompt]);

  const handleSave = useCallback(
    async (value: string) => {
      if (!orgId || !collectionId) {
        return;
      }
      setIsSaving(true);
      setError(null);
      try {
        const normalized = value.trim();
        const result = await updateCollectionChatSystemPrompt(
          orgId,
          collectionId,
          normalized ? normalized : null,
        );
        if ('detail' in result) {
          setError(result.detail);
          notification.error(result.detail);
          return;
        }
        setPromptData(result);
        setInputValue(result.prompt);
        onPromptUpdated?.(result.source, result.prompt_id ?? null);
        notification.success(
          result.source === 'custom'
            ? 'Custom chat prompt saved.'
            : 'Chat prompt reverted to the default template.',
        );
        onOpenChange(false);
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : 'Unable to save chat system prompt.';
        setError(message);
        notification.error(message);
      } finally {
        setIsSaving(false);
      }
    },
    [collectionId, notification, onOpenChange, onPromptUpdated, orgId],
  );

  const handleSaveClick = useCallback(() => {
    void handleSave(inputValue);
  }, [handleSave, inputValue]);

  const handleReset = useCallback(() => {
    if (!confirmingClear) {
      setConfirmingClear(true);
      return;
    }
    void handleSave('');
    setConfirmingClear(false);
  }, [confirmingClear, handleSave]);

  const handleCancelClear = useCallback(() => {
    setConfirmingClear(false);
  }, []);

  const footer = confirmingClear ? (
    <div className='space-y-3'>
      <div className='rounded-lg border border-destructive/50 bg-destructive/10 p-4'>
        <p className='text-sm text-foreground'>
          This will remove your custom instructions forever and revert to the
          default system prompt.
        </p>
      </div>
      <div className='flex items-center justify-end gap-2'>
        <Button variant='outline' onClick={handleCancelClear}>
          Cancel
        </Button>
        <Button variant='destructive' onClick={handleReset} disabled={isSaving}>
          {isSaving ? <Loader2 className='mr-2 h-4 w-4 animate-spin' /> : null}
          Yes, clear it
        </Button>
      </div>
    </div>
  ) : (
    <div className='flex items-center justify-between gap-3'>
      <Button
        type='button'
        variant='ghost'
        onClick={handleReset}
        disabled={isSaving || isLoading || (!hasCustomPrompt && !inputValue)}
      >
        <RotateCcw className='mr-2 h-4 w-4' />
        Clear customization
      </Button>
      <div className='flex items-center gap-2'>
        {isLoading && (
          <div className='flex items-center gap-2 text-sm text-muted-foreground'>
            <Loader2 className='h-4 w-4 animate-spin' />
            Loading prompt…
          </div>
        )}
        <Button
          type='button'
          onClick={handleSaveClick}
          disabled={isSaving || isLoading}
        >
          {isSaving ? <Loader2 className='mr-2 h-4 w-4 animate-spin' /> : null}
          Save changes
        </Button>
      </div>
    </div>
  );

  return (
    <Modal
      title='Collection chat system prompt'
      description={`Control how the assistant answers questions about ${targetLabel}.`}
      isDialogOpen={open}
      setIsDialogOpen={onOpenChange}
      contentClassName='sm:max-w-2xl'
      footer={footer}
    >
      <div className='space-y-4'>
        <div className='rounded-lg border bg-muted/40 p-3 text-sm'>
          <div className='flex items-center gap-2 text-foreground'>
            <Sparkles className='h-4 w-4 text-primary' />
            <span className='font-medium'>
              {hasCustomPrompt
                ? 'Custom prompt active'
                : 'Using default prompt'}
            </span>
            <Badge variant={hasCustomPrompt ? 'outline' : 'secondary'}>
              {hasCustomPrompt ? 'Custom' : 'Default'}
            </Badge>
          </div>
          <p className='mt-1 text-muted-foreground'>{helperText}</p>
        </div>

        {error && (
          <p className='text-sm text-destructive' role='alert'>
            {error}
          </p>
        )}

        <Textarea
          value={inputValue}
          onChange={(event) => setInputValue(event.target.value)}
          rows={12}
          disabled={isLoading || isSaving}
          placeholder='Describe how the assistant should behave...'
        />

        <p className='text-xs text-muted-foreground'>
          Use variables like {'{org_id}'} or {'{collection_name}'} if you want
          to keep the default context interpolation.
        </p>
      </div>
    </Modal>
  );
};
